import { useEffect, useRef, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setModalxComponent } from "components/modalx/modalx.slice";
import BoardCard from "components/project-boards/BoardCard";
import BoardRow from "components/project-boards/BoardRow";
import BoardSection from "components/project-boards/BoardSection";
import BoardsToolbar from "components/project-boards/BoardsToolbar";
import { StarIcon, PinIcon, ClockIcon } from "components/project-boards/icons";
import {
  fetchBoardsHomeThunk,
  fetchAllFirstPage,
  fetchAllNextPage,
  setSearch,
  setViewMode,
  toggleStar,
  togglePin,
  resetBoards,
} from "./project-boards.slice";
import { BoardCard as BoardCardType, ViewMode } from "./types";

const SKELETON_KEYS = Array.from({ length: 8 }, (_, i) => `sk-${i}`);

function BoardsSkeleton({ viewMode }: { viewMode: ViewMode }) {
  if (viewMode === "list") {
    return (
      <div className="flex flex-col gap-2">
        {SKELETON_KEYS.map((k) => (
          <div
            key={k}
            className="h-11 animate-pulse rounded-lg bg-[var(--p-color-bg-surface-secondary)]"
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
      {SKELETON_KEYS.map((k) => (
        <div
          key={k}
          className="h-[104px] animate-pulse rounded-lg bg-[var(--p-color-bg-surface-secondary)]"
        />
      ))}
    </div>
  );
}

function ProjectBoards() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId = "" } = useParams();

  const sections = useAppSelector((s) => s.projectBoardsReducer.sections);
  const all = useAppSelector((s) => s.projectBoardsReducer.all);
  const allPagination = useAppSelector((s) => s.projectBoardsReducer.allPagination);
  const isLoading = useAppSelector((s) => s.projectBoardsReducer.isLoading);
  const isLoadingMore = useAppSelector((s) => s.projectBoardsReducer.isLoadingMore);
  const isError = useAppSelector((s) => s.projectBoardsReducer.isError);
  const search = useAppSelector((s) => s.projectBoardsReducer.search);
  const viewMode = useAppSelector((s) => s.projectBoardsReducer.viewMode);

  const [searchInput, setSearchInput] = useState(search);

  // On an actual company switch, drop the previous company's boards/sections/
  // search before the refetch effects below run, so stale boards can't show
  // under the new URL. Skipped on first mount (ref starts at the current id) and
  // on search changes (companyId unchanged) — those keep their smooth behavior.
  const loadedCompanyRef = useRef(companyId);
  useEffect(() => {
    if (loadedCompanyRef.current === companyId) return;
    loadedCompanyRef.current = companyId;
    setSearchInput("");
    dispatch(resetBoards());
  }, [companyId, dispatch]);

  // Home sections (pinned/starred/recent) on mount / company change.
  useEffect(() => {
    dispatch(fetchBoardsHomeThunk(companyId));
  }, [dispatch, companyId]);

  // Debounce the search box → store search (resets the All list).
  useEffect(() => {
    if (searchInput.trim() === search) return undefined;
    const id = setTimeout(() => dispatch(setSearch(searchInput.trim())), 300);
    return () => clearTimeout(id);
  }, [searchInput, search, dispatch]);

  // (Re)load the first page of "All" when company or search changes.
  useEffect(() => {
    dispatch(fetchAllFirstPage(companyId));
  }, [dispatch, companyId, search]);

  // Infinite scroll: load the next page when the sentinel comes into view.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) dispatch(fetchAllNextPage(companyId));
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [dispatch, companyId, allPagination, isLoadingMore]);

  const onOpen = useCallback(
    (id: string) => navigate(`/app/${companyId}/project-boards/${id}`),
    [navigate, companyId]
  );
  const onToggleStar = useCallback(
    (board: BoardCardType) => dispatch(toggleStar(companyId, board)),
    [dispatch, companyId]
  );
  const onTogglePin = useCallback(
    (board: BoardCardType) => dispatch(togglePin(companyId, board)),
    [dispatch, companyId]
  );
  const onCreate = () => dispatch(setModalxComponent("ProjectBoardForm"));

  const renderBoards = (boards: BoardCardType[]) =>
    viewMode === "grid" ? (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        {boards.map((b) => (
          <BoardCard
            key={b.id}
            board={b}
            onOpen={onOpen}
            onToggleStar={onToggleStar}
            onTogglePin={onTogglePin}
          />
        ))}
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        {boards.map((b) => (
          <BoardRow
            key={b.id}
            board={b}
            onOpen={onOpen}
            onToggleStar={onToggleStar}
            onTogglePin={onTogglePin}
          />
        ))}
      </div>
    );

  const showSkeleton = isLoading && all.length === 0;
  const showSections = !search;

  return (
    <div className="w-full">
      {/* The parent <main> has lg:pr-16 (64px reserved for the fixed online-users
          rail), so a plain full-width bar stops short on the right. The bleed strip
          below fills that reserved 64px with the same gray; the toolbar content keeps
          its normal centered width so it stays aligned with the cards below. */}
      <div className="z-10 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg)] sm:sticky sm:top-0">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-full hidden w-16 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg)] lg:block"
        />
        <div className="mx-auto w-full max-w-6xl px-6 py-4">
          <BoardsToolbar
            search={searchInput}
            onSearchChange={setSearchInput}
            viewMode={viewMode}
            onViewModeChange={(m) => dispatch(setViewMode(m))}
            onCreate={onCreate}
            createLabel={t("project-boards-create", {
              defaultValue: "Create board",
            })}
            searchPlaceholder={t("search_your_boards", {
              defaultValue: "Search your boards",
            })}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-6 pb-6 pt-6">
      {showSections && sections.pinned.length > 0 && (
        <BoardSection
          sectionKey="pinned"
          icon={<PinIcon />}
          title={t("pinned", { defaultValue: "Pinned" })}
          count={sections.pinned.length}
        >
          {renderBoards(sections.pinned)}
        </BoardSection>
      )}

      {showSections && sections.starred.length > 0 && (
        <BoardSection
          sectionKey="starred"
          icon={<StarIcon filled />}
          title={t("starred", { defaultValue: "Starred" })}
          count={sections.starred.length}
        >
          {renderBoards(sections.starred)}
        </BoardSection>
      )}

      {showSections && sections.recent.length > 0 && (
        <BoardSection
          sectionKey="recent"
          icon={<ClockIcon />}
          title={t("recent", { defaultValue: "Recent" })}
          count={sections.recent.length}
        >
          {renderBoards(sections.recent)}
        </BoardSection>
      )}

      <BoardSection
        sectionKey="all"
        icon={null}
        title={
          search
            ? t("search_results", { defaultValue: "Search results" })
            : t("all_boards", { defaultValue: "All boards" })
        }
        count={allPagination?.total}
      >
        {showSkeleton ? (
          <BoardsSkeleton viewMode={viewMode} />
        ) : isError ? (
          // A failed (re)load must not leave the previous query's boards on
          // screen, where they'd look like results for the new search.
          <div className="py-8 text-center">
            <p className="text-sm text-[var(--p-color-text-secondary)]">
              {t("board_load_failed", { defaultValue: "Couldn't load boards." })}
            </p>
            <button
              type="button"
              onClick={() => dispatch(fetchAllFirstPage(companyId))}
              className="mt-2 rounded-md bg-[var(--p-color-bg-fill-brand)] px-3 py-1.5 text-sm font-semibold text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)]"
            >
              {t("retry", { defaultValue: "Retry" })}
            </button>
          </div>
        ) : all.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--p-color-text-secondary)]">
            {t("no_results", { defaultValue: "No boards found" })}
          </p>
        ) : (
          renderBoards(all)
        )}

        <div ref={sentinelRef} className="h-6" />
        {isLoadingMore && (
          <p className="py-2 text-center text-xs text-[var(--p-color-text-secondary)]">
            {t("loading", { defaultValue: "Loading…" })}
          </p>
        )}
      </BoardSection>
      </div>
    </div>
  );
}

export default ProjectBoards;
